import DOMPurify from 'dompurify';
import { Remarkable } from 'remarkable';

/**
 * Singleton class for managing Markdown rendering configuration
 * Handles Remarkable and DOMPurify setup with security settings
 */
class MarkdownConfig {
  constructor() {
    this._markdownRenderer = null;
    this._isDOMPurifyConfigured = false;
  }

  /**
   * Get or create the Remarkable markdown renderer instance
   * @returns {Remarkable} Configured Remarkable instance
   */
  getMarkdownRenderer() {
    if (!this._markdownRenderer) {
      this._markdownRenderer = new Remarkable({
        breaks: true,
        html: false,
        linkify: true,
      });

      /* eslint-disable camelcase */
      // NOTE: eslint is disabled for this block because remarkable uses snake_case for its rules
      // We need to override link_open to add target="_blank" and rel="noopener noreferrer" for security
      this._markdownRenderer.renderer.rules.link_open = (tokens, idx) => {
        const href = tokens[idx].href;
        const title = tokens[idx].title ? ` title="${tokens[idx].title}"` : '';
        return `<a href="${href}"${title} target="_blank" rel="noopener noreferrer">`;
      };
      /* eslint-enable camelcase */
    }

    return this._markdownRenderer;
  }

  /**
   * Configure DOMPurify to add security attributes to all links
   * This is a one-time configuration
   */
  configureDOMPurify() {
    if (!this._isDOMPurifyConfigured) {
      DOMPurify.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A') {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      });
      this._isDOMPurifyConfigured = true;
    }
  }

  /**
   * Sanitize HTML content using DOMPurify
   * @param {string} html - HTML content to sanitize
   * @returns {string} - Sanitized HTML
   */
  sanitizeHtml(html) {
    this.configureDOMPurify();
    return DOMPurify.sanitize(html);
  }
}

// INFO: This utility is used across the app for rendering markdown in messages,
// so we export a singleton instance
export default new MarkdownConfig();
